import { useEffect, useState } from 'react';
import './AckBanner.css';
import type { GMDispatchSnapshot } from '../../hooks/useGMDispatcher';

/**
 * V6.2.B Voice GM (2026-06-02) — atmospheric tool acknowledgement.
 *
 * Auto-dismissing non-interactive banner that surfaces when the GM
 * dispatcher fires an atmospheric tool (no view mutation, no undo
 * needed) so the player gets visual confirmation beyond the voice log.
 *
 * Tool → friendly headline + technical sub-line:
 *   describe_npc(npc_id)           → "GM described <npc>"     · describe_npc(npc_id: 'X')
 *   look_around(focus?)            → "GM looked around"        · look_around({focus: 'X'} | {})
 *   check_inventory()              → "GM checked inventory"    · check_inventory()
 *   recall_briefing(topic?)        → "GM recalled the briefing" · recall_briefing({topic: 'X'} | {})
 *   ask_npc(npc_id, question)      → "GM asked <npc>"          · ask_npc(npc_id: 'X')
 *
 * Returns null when:
 *   - snapshot is null
 *   - snapshot.action is null
 *   - snapshot.action.tool is one of the view-mutating tools (UndoBanner
 *     covers those)
 */

export const ATMOSPHERIC_TOOLS = [
  'describe_npc',
  'look_around',
  'check_inventory',
  'recall_briefing',
  'ask_npc',
] as const;

export type AtmosphericTool = (typeof ATMOSPHERIC_TOOLS)[number];

function isAtmosphericTool(tool: string | null | undefined): tool is AtmosphericTool {
  return (
    typeof tool === 'string' &&
    (ATMOSPHERIC_TOOLS as readonly string[]).includes(tool)
  );
}

function humanize(npcId: string): string {
  if (npcId.length === 0) return 'someone';
  return npcId
    .split('_')
    .filter((s) => s.length > 0)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export interface AckMessages {
  headline: string;
  technical: string;
}

export function buildAckMessages(
  tool: AtmosphericTool,
  args: Record<string, unknown>,
): AckMessages {
  switch (tool) {
    case 'describe_npc': {
      const id = typeof args.npc_id === 'string' ? args.npc_id : '';
      return {
        headline: `GM described ${humanize(id)}`,
        technical: `describe_npc(npc_id: '${id}')`,
      };
    }
    case 'look_around': {
      const focus = typeof args.focus === 'string' ? args.focus : '';
      return {
        headline: focus.length > 0 ? `GM looked around (${focus})` : 'GM looked around',
        technical: focus.length > 0 ? `look_around({focus: '${focus}'})` : 'look_around()',
      };
    }
    case 'check_inventory':
      return {
        headline: 'GM checked inventory',
        technical: 'check_inventory()',
      };
    case 'recall_briefing': {
      const topic = typeof args.topic === 'string' ? args.topic : '';
      return {
        headline: topic.length > 0 ? `GM recalled the briefing (${topic})` : 'GM recalled the briefing',
        technical: topic.length > 0 ? `recall_briefing({topic: '${topic}'})` : 'recall_briefing()',
      };
    }
    case 'ask_npc': {
      const id = typeof args.npc_id === 'string' ? args.npc_id : '';
      return {
        headline: `GM asked ${humanize(id)}`,
        technical: `ask_npc(npc_id: '${id}')`,
      };
    }
  }
}

export interface AckBannerProps {
  snapshot: GMDispatchSnapshot | null;
  dismissMs?: number;
  onExpire: () => void;
}

export function AckBanner({ snapshot, dismissMs = 2000, onExpire }: AckBannerProps) {
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    if (snapshot === null || snapshot.action === null) {
      setVisible(false);
      return;
    }
    if (!isAtmosphericTool(snapshot.action.tool)) {
      setVisible(false);
      return;
    }
    setVisible(true);
    const timer = setTimeout(() => {
      setVisible(false);
      onExpire();
    }, dismissMs);
    return () => clearTimeout(timer);
  }, [snapshot, dismissMs, onExpire]);

  if (
    snapshot === null ||
    snapshot.action === null ||
    !isAtmosphericTool(snapshot.action.tool) ||
    !visible
  ) {
    return null;
  }

  const tool = snapshot.action.tool;
  const messages = buildAckMessages(tool, snapshot.action.args);

  return (
    <div
      className="br-ack-banner"
      data-testid="br-ack-banner"
      data-tool={tool}
      role="status"
      aria-live="polite"
    >
      <span
        className="br-ack-banner-headline"
        data-testid="br-ack-banner-headline"
      >
        {messages.headline}
      </span>
      <span
        className="br-ack-banner-technical"
        data-testid="br-ack-banner-technical"
      >
        {messages.technical}
      </span>
    </div>
  );
}

export default AckBanner;
